import liff from '@line/liff'
import formatAmount from './formatAmount'

// Build a LINE Flex bubble that shares a creditor's 匯款資訊 (bank-transfer note)
// when asking a debtor (ower) to repay. Mirrors the bubble style used by
// PaymentForm.buildPaymentSuccessMessage so it looks consistent in chat.
//
// The returned object is a `liff.sendMessages` / `liff.shareTargetPicker` message.
const buildRemittanceFlex = ({ creditorName, remittanceInfo, owerName, amount, symbol = '$', exponent = 0 }) => {
  const bodyContents = [
    {
      type: 'text',
      text: '匯款資訊',
      color: '#C8842A',
      size: 'xs',
      weight: 'bold',
    },
    {
      type: 'text',
      text: owerName ? `${owerName}，請匯款給 ${creditorName} 🙏` : `請匯款給 ${creditorName} 🙏`,
      size: 'md',
      weight: 'bold',
      wrap: true,
      margin: 'sm',
    },
    amount != null && {
      type: 'box',
      layout: 'baseline',
      margin: 'md',
      contents: [
        { type: 'text', text: '應付金額', size: 'sm', color: '#555555', flex: 0 },
        { type: 'text', text: formatAmount(amount, exponent, symbol), size: 'sm', weight: 'bold', align: 'end', color: '#126951' },
      ],
    },
    { type: 'separator', margin: 'md' },
    {
      type: 'box',
      layout: 'vertical',
      margin: 'md',
      contents: [
        { type: 'text', text: remittanceInfo, size: 'sm', wrap: true },
      ],
    },
  ].filter(Boolean)

  const contents = {
    type: 'bubble',
    size: 'mega',
    body: {
      type: 'box',
      layout: 'vertical',
      contents: bodyContents,
      backgroundColor: '#ffffff',
      paddingAll: 'xl',
    },
  }

  return {
    type: 'flex',
    altText: `${creditorName} 的匯款資訊`,
    contents,
  }
}

// Plain-text version of the 匯款資訊, used for navigator.share / clipboard
// fallbacks when the LINE in-client Flex send isn't available.
const buildRemittanceText = ({ creditorName, remittanceInfo, amount, symbol = '$', exponent = 0 }) => {
  const lines = [`請匯款給 ${creditorName} 🙏`]
  if (amount != null) lines.push(`應付金額：${formatAmount(amount, exponent, symbol)}`)
  lines.push('', remittanceInfo)
  return lines.join('\n')
}

// Share a 收款者's (creditor) 匯款資訊 wherever the user wants. Precedence:
//   1) inside LINE  → shareTargetPicker (let them pick a chat), else send to current group
//   2) in a browser → OS share sheet (navigator.share) to other apps
//   3) fallback      → copy to clipboard, else alert
const shareRemittance = ({ creditorName, remittanceInfo, amount, symbol = '$', exponent = 0 }) => {
  if (!remittanceInfo) {
    window.alert(`${creditorName} 還沒設定匯款資訊，可在 LINE 輸入「匯款資訊 + 帳號」設定`)
    return
  }

  const flex = buildRemittanceFlex({ creditorName, remittanceInfo, amount, symbol, exponent })
  const text = buildRemittanceText({ creditorName, remittanceInfo, amount, symbol, exponent })

  if (liff && liff.isInClient && liff.isInClient()) {
    const canPick = liff.isApiAvailable && liff.isApiAvailable('shareTargetPicker')
    if (canPick) {
      return liff.shareTargetPicker([flex]).catch(() => liff.sendMessages([flex]).catch(() => {}))
    }
    return liff.sendMessages([flex]).then(() => liff.closeWindow && liff.closeWindow()).catch(() => {})
  }

  if (navigator.share) {
    return navigator.share({ title: `${creditorName} 的匯款資訊`, text }).catch(() => {})
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).then(() => window.alert('匯款資訊已複製，可貼到任何聊天室')).catch(() => {})
  }

  window.alert(text)
}

export default buildRemittanceFlex
export { buildRemittanceFlex, buildRemittanceText, shareRemittance }
